import React from "react";

export interface ImageCanvasProps {
  image: any;
  imageRotate: number;
  imageScaleY: number;
  imageScaleX: number;
  isDrawMode?: boolean;
  isEraseMode?: boolean;
  penStyle?: {
    color: string;
    lineWidth: number;
  };
  eraserSize?: number;
  handleMountCanvas: (canvas: any) => void;
  onDrawingStart?: () => void;
}

class ImageCanvas extends React.Component<ImageCanvasProps> {
  canvasRef;
  imageRef: any;
  drawingCanvas: HTMLCanvasElement | null = null;
  drawingContext: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private rafId: number | null = null;

  constructor(props: any) {
    super(props);

    this.canvasRef = React.createRef<HTMLCanvasElement>();
    this.imageRef = React.createRef<HTMLImageElement>();
  }

  componentDidMount(): void {
    const canvas = this.canvasRef.current!;

    this.imageRef.current = new Image();
    const image = this.imageRef.current;

    image.onload = () => {
      canvas.width = image.width;
      canvas.height = image.height;

      this.createDrawingCanvas();

      this.drawImageAndCanvas();
    };
    image.crossOrigin = "*";
    image.src = this.props.image.imageUrl + "?liex=anonymous";

    this.props.handleMountCanvas(canvas);

    canvas.addEventListener('mousedown', this.handleMouseDown);
    canvas.addEventListener('mousemove', this.handleMouseMove);
    canvas.addEventListener('mouseup', this.handleMouseUp);
    canvas.addEventListener('mouseout', this.handleMouseUp);

    canvas.addEventListener('touchstart', this.handleTouchStart);
    canvas.addEventListener('touchmove', this.handleTouchMove);
    canvas.addEventListener('touchend', this.handleTouchEnd);
  }

  componentWillUnmount(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    const canvas = this.canvasRef.current;
    if (canvas) {
      canvas.removeEventListener('mousedown', this.handleMouseDown);
      canvas.removeEventListener('mousemove', this.handleMouseMove);
      canvas.removeEventListener('mouseup', this.handleMouseUp);
      canvas.removeEventListener('mouseout', this.handleMouseUp);
      canvas.removeEventListener('touchstart', this.handleTouchStart);
      canvas.removeEventListener('touchmove', this.handleTouchMove);
      canvas.removeEventListener('touchend', this.handleTouchEnd);
    }
  }

  componentDidUpdate(
      prevProps: Readonly<ImageCanvasProps>,
  ): void {
    if (
        prevProps.imageRotate !== this.props.imageRotate ||
        prevProps.imageScaleX !== this.props.imageScaleX ||
        prevProps.imageScaleY !== this.props.imageScaleY
    ) {
      this.drawImageAndCanvas();
    }
  }

  createDrawingCanvas = () => {
    const canvas = this.canvasRef.current!;
    if (!this.drawingCanvas) {
      this.drawingCanvas = document.createElement('canvas');
    }
    this.drawingCanvas.width = canvas.width;
    this.drawingCanvas.height = canvas.height;
    this.drawingContext = this.drawingCanvas.getContext('2d')!;

    this.drawingContext.lineCap = 'round';
    this.drawingContext.lineJoin = 'round';
    this.drawingContext.globalCompositeOperation = 'source-over';
  };

  drawImageAndCanvas = () => {
    const image = this.imageRef.current;
    const canvas = this.canvasRef.current!;

    if (!image || !canvas) return;

    if (Math.abs(this.props.imageRotate).toString() === "90" || Math.abs(this.props.imageRotate).toString() === "270") {
      canvas.width = image?.height;
      canvas.height = image?.width;
    }
    if (Math.abs(this.props.imageRotate).toString() === "0" || Math.abs(this.props.imageRotate).toString() === "180") {
      canvas.width = image?.width;
      canvas.height = image?.height;
    }

    if (this.drawingCanvas && (this.drawingCanvas.width !== canvas.width || this.drawingCanvas.height !== canvas.height)) {
      const tempCanvas = document.createElement('canvas');
      const tempContext = tempCanvas.getContext('2d')!;
      tempCanvas.width = this.drawingCanvas.width;
      tempCanvas.height = this.drawingCanvas.height;
      tempContext.drawImage(this.drawingCanvas, 0, 0);

      this.drawingCanvas.width = canvas.width;
      this.drawingCanvas.height = canvas.height;
      this.drawingContext = this.drawingCanvas.getContext('2d')!;
      this.drawingContext.lineCap = 'round';
      this.drawingContext.lineJoin = 'round';
      this.drawingContext.drawImage(tempCanvas, 0, 0);
    }

    const context = canvas?.getContext("2d")!;
    const hRatio = canvas?.width / image?.width;
    const vRatio = canvas?.height / image?.height;
    const ratio = Math.min(hRatio, vRatio);

    context?.clearRect(0, 0, canvas.width, canvas.height);
    context?.save();
    context?.translate(canvas.width / 2, canvas.height / 2);
    context?.rotate((this.props.imageRotate * Math.PI) / 180);
    context?.scale(this.props.imageScaleY, this.props.imageScaleX);
    context?.drawImage(
        image,
        -(image.width * ratio) / 2,
        -(image.height * ratio) / 2,
        image.width * ratio,
        image.height * ratio
    );
    context?.restore();

    if (this.drawingCanvas) {
      context.drawImage(this.drawingCanvas, 0, 0);
    }
  };

  getMousePos = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
    const canvas = this.canvasRef.current!;
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if (e instanceof MouseEvent) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  startDrawing = (x: number, y: number) => {
    if ((!this.props.isDrawMode && !this.props.isEraseMode) || !this.drawingContext) return;

    if (this.props.onDrawingStart) {
      this.props.onDrawingStart();
    }

    this.isDrawing = true;

    if (this.props.isEraseMode) {
      this.drawingContext.globalCompositeOperation = 'destination-out';
      this.drawingContext.lineWidth = this.props.eraserSize || 10;
    } else {
      this.drawingContext.strokeStyle = this.props.penStyle?.color || '#FF0000';
      this.drawingContext.lineWidth = this.props.penStyle?.lineWidth || 3;
      this.drawingContext.globalCompositeOperation = 'source-over';
    }

    this.drawingContext.beginPath();
    this.drawingContext.moveTo(x, y);

    this.drawingContext.lineTo(x + 0.1, y + 0.1);
    this.drawingContext.stroke();

    this.drawImageAndCanvas();
  };

  draw = (x: number, y: number) => {
    if (!this.isDrawing || (!this.props.isDrawMode && !this.props.isEraseMode) || !this.drawingContext) return;

    this.drawingContext.lineTo(x, y);
    this.drawingContext.stroke();

    if (!this.rafId) {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.drawImageAndCanvas();
      });
    }
  };

  stopDrawing = () => {
    if (!this.isDrawing) return;

    this.isDrawing = false;

    if (this.drawingContext) {
      this.drawingContext.closePath();
    }
  };

  handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    const pos = this.getMousePos(e);
    this.startDrawing(pos.x, pos.y);
  };

  handleMouseMove = (e: MouseEvent) => {
    e.preventDefault();
    const pos = this.getMousePos(e);
    this.draw(pos.x, pos.y);
  };

  handleMouseUp = (e: MouseEvent) => {
    e.preventDefault();
    this.stopDrawing();
  };

  handleTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    const pos = this.getMousePos(e);
    this.startDrawing(pos.x, pos.y);
  };

  handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    const pos = this.getMousePos(e);
    this.draw(pos.x, pos.y);
  };

  handleTouchEnd = (e: TouchEvent) => {
    e.preventDefault();
    this.stopDrawing();
  };

  render() {
    const getCursor = () => {
      if (this.props.isDrawMode) return 'crosshair';
      if (this.props.isEraseMode) return 'grab';
      return 'default';
    };

    return (
        <canvas
            ref={this.canvasRef}
            style={{
              cursor: getCursor(),
              touchAction: 'none',
              maxWidth: '100%',
              height: 'auto'
            }}
        />
    );
  }
}

export default ImageCanvas;
